import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import adminService from '../services/admin.service';
import type { CreateCustomerUserRequest, UpdateCustomerUserRequest, TrustedDevicePolicyInput } from '../types';

// === Customer Queries ===
export function useCustomers(search?: string) {
  return useQuery({
    queryKey: ['customers', search],
    queryFn: () => adminService.getCustomers(search),
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => adminService.createCustomer(name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
}

// === License Queries ===
export function useLicenses(customerId?: string) {
  return useQuery({
    queryKey: ['licenses', customerId],
    queryFn: () => adminService.getLicenses(customerId),
  });
}

export function useCreateLicense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, productId }: { customerId: string; productId: string }) =>
      adminService.createLicense(customerId, productId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['licenses'] }),
  });
}

// === User Queries (Global) ===
export function useAdminUsers(enabled = true) {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminService.getUsers(),
    enabled,
  });
}

// === Customer User Queries ===
export function useCustomerUsers(customerId: string) {
  return useQuery({
    queryKey: ['customer-users', customerId],
    queryFn: () => adminService.getCustomerUsers(customerId),
    enabled: !!customerId,
  });
}

export function useAddCustomerUser(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateCustomerUserRequest) =>
      adminService.addCustomerUser(customerId, request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer-users', customerId] }),
  });
}

export function useUpdateCustomerUser(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, request }: { userId: string; request: UpdateCustomerUserRequest }) =>
      adminService.updateCustomerUser(customerId, userId, request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer-users', customerId] }),
  });
}

export function useRemoveCustomerUser(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminService.removeCustomerUser(customerId, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer-users', customerId] }),
  });
}

// === Product Queries ===
export function useProducts() {
  return useQuery({
    queryKey: ['products'],
    queryFn: () => adminService.getProducts(),
  });
}

// === Trusted device queries (2FA "husk denne enheten") ===
export function useTrustedDevices(username: string, enabled = true) {
  return useQuery({
    queryKey: ['trusted-devices', username],
    queryFn: () => adminService.getTrustedDevices(username),
    enabled: enabled && !!username,
  });
}

export function useRevokeTrustedDevices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (username: string) => adminService.revokeTrustedDevices(username),
    onSuccess: (_result, username) => {
      // The per-user list and the count on the user list both change.
      queryClient.invalidateQueries({ queryKey: ['trusted-devices', username] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export function useRevokeTrustedDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ username, deviceId }: { username: string; deviceId: string }) =>
      adminService.revokeTrustedDevice(username, deviceId),
    onSuccess: (_result, { username }) => {
      queryClient.invalidateQueries({ queryKey: ['trusted-devices', username] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

// === Trusted device policy (GlobalAdmin) ===
export function useTrustedDevicePolicy(enabled = true) {
  return useQuery({
    queryKey: ['trusted-device-policy'],
    queryFn: () => adminService.getTrustedDevicePolicy(),
    enabled,
  });
}

export function useUpdateTrustedDevicePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (policy: TrustedDevicePolicyInput) => adminService.updateTrustedDevicePolicy(policy),
    onSuccess: (saved) => queryClient.setQueryData(['trusted-device-policy'], saved),
  });
}

export function useRevokeAllTrustedDevices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => adminService.revokeAllTrustedDevices(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trusted-devices'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}
